import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PhoneCall, Phone, MessageCircle, Instagram, Check } from "lucide-react";
import { getConversations, attendConversation } from "@/lib/dentiumApi";
import { AttendedHistory } from "@/components/AttendedHistory";

const CH_ICON = { whatsapp: MessageCircle, instagram: Instagram };
const CH_COLOR = { whatsapp: "#25D366", instagram: "#E1306C" };

export default function LlamadasPage() {
  const qc = useQueryClient();
  const { data: conversations = [] } = useQuery({
    queryKey: ["conversations", "todos", "todos"],
    queryFn: () => getConversations("todos", "todos"),
    refetchInterval: 5000,
  });

  const attend = useMutation({
    mutationFn: attendConversation,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["conversations"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      toast.success("Llamada marcada como atendida");
    },
  });

  const pending = conversations.filter((c) => c.status === "cita_pendiente" && c.lead);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1100px] mx-auto space-y-6 overflow-y-auto h-full" data-testid="llamadas-page">
      {/* Pendientes de llamar */}
      <div className="rounded-2xl bg-white border border-[#E7E2DA] p-4 sm:p-5" data-testid="pending-calls-panel">
        <div className="flex items-center gap-2 mb-4">
          <span className="h-9 w-9 rounded-xl bg-[#F1EBE1] flex items-center justify-center">
            <PhoneCall className="h-4.5 w-4.5 text-[#1A1A1A]" />
          </span>
          <div>
            <h2 className="text-base font-medium text-[#1A1A1A]">Contactos pendientes de llamar</h2>
            <p className="text-xs text-[#8A7E70]">Leads recogidos por el bot que esperan una llamada del equipo</p>
          </div>
          <span className="ml-auto h-7 min-w-7 px-2 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center">
            {pending.length}
          </span>
        </div>

        <div className="space-y-2">
          {pending.length === 0 && (
            <div className="py-8 text-center text-xs text-[#A79E90]">No hay llamadas pendientes ahora mismo.</div>
          )}
          {pending.map((c) => {
            const Icon = CH_ICON[c.channel] || MessageCircle;
            return (
              <div key={c.id} data-testid="pending-call-item" className="flex items-center gap-3 rounded-xl border border-[#EDE7DE] bg-[#FAF8F5]/60 px-3 py-2.5">
                <span className="h-9 w-9 rounded-full flex items-center justify-center text-white shrink-0" style={{ background: CH_COLOR[c.channel] || "#1A1A1A" }}>
                  <Icon className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-[#1A1A1A] truncate">{c.lead?.name}</div>
                  <div className="text-[11px] text-[#8A7E70] flex items-center gap-1.5">
                    <Phone className="h-3 w-3" /> {c.lead?.phone} <span className="text-[#B8ADA0]">·</span> {c.topic}
                  </div>
                </div>
                <button
                  data-testid="btn-attend-call"
                  onClick={() => attend.mutate(c.id)}
                  disabled={attend.isPending}
                  className="inline-flex items-center gap-1 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-full px-3 py-1.5 transition-colors disabled:opacity-50 shrink-0"
                >
                  <Check className="h-3.5 w-3.5" /> Atendida
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Historial de llamadas atendidas */}
      <AttendedHistory />
    </div>
  );
}
